import React, { useEffect, useState, useCallback } from "react";
import PublicLayout from "../components/PublicLayout";
import { api, formatError } from "../lib/api";
import { log } from "../lib/log";
import { useAuth } from "../context/AuthContext";
import { useForceLang } from "../i18n";
import { toast } from "sonner";
import { Check, X, Settings2, Truck, Printer, History, LineChart as LineIcon, Trash2, FileText, Upload, PlayCircle, Mail, Instagram } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { Kpi, Row } from "../components/admin/AdminKit";
import AdminProofUpload from "../components/admin/AdminProofUpload";
import AdminComicsManager from "../components/admin/AdminComicsManager";
import AdminSchedules from "../components/admin/AdminSchedules";
import AdminWelcomeImages from "../components/admin/AdminWelcomeImages";
import AdminRazorpayKeys from "../components/admin/AdminRazorpayKeys";
import AdminAudioComicsSettings from "../components/admin/AdminAudioComicsSettings";
import AdminEComics from "../components/admin/AdminEComics";
import AdminEComicsSales from "../components/admin/AdminEComicsSales";

export default function AdminPanel() {
  useForceLang("en");
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("reports");
  const [schools, setSchools] = useState([]);
  const [orders, setOrders] = useState([]);
  const [batches, setBatches] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [donations, setDonations] = useState([]);
  const [reports, setReports] = useState(null);
  const [pledges, setPledges] = useState([]);
  const [videos, setVideos] = useState([]);
  const [comics, setComics] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [scheduleFilters, setScheduleFilters] = useState({ school_id: "", donor_id: "", date: "", status: "" });
  const [settings, setSettings] = useState({ price_per_set: 500, minimum_donation_sets: 20, tax_80g_number: "", aapti_pan: "", donor_emails_enabled: true });
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    const [s, o, b, p, d, r, st, h, pl, vv] = await Promise.all([
      api.get("/admin/schools").then(r=>r.data).catch(()=>[]),
      api.get("/admin/orders").then(r=>r.data).catch(()=>[]),
      api.get("/admin/batches").then(r=>r.data).catch(()=>[]),
      api.get("/admin/proofs").then(r=>r.data).catch(()=>[]),
      api.get("/admin/donations").then(r=>r.data).catch(()=>[]),
      api.get("/admin/reports").then(r=>r.data).catch(()=>null),
      api.get("/settings").then(r=>r.data).catch(()=>({})),
      api.get("/settings/history").then(r=>r.data).catch(()=>[]),
      api.get("/admin/pledges").then(r=>r.data).catch(()=>[]),
      api.get("/admin/impact-videos").then(r=>r.data).catch(()=>[]),
    ]);
    setSchools(s); setOrders(o); setBatches(b); setProofs(p); setDonations(d);
    setReports(r);
    setSettings({
      price_per_set: st.price_per_set||500,
      minimum_donation_sets: st.minimum_donation_sets||20,
      tax_80g_number: st.tax_80g_number||"",
      aapti_pan: st.aapti_pan||"",
      donor_emails_enabled: st.donor_emails_enabled !== false,
    });
    setHistory(h);
    setPledges(pl);
    setVideos(vv);
  }, []);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);
  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/comics")
        .then(r => setComics(r.data))
        .catch(err => log.warn("[admin] comics load failed:", err?.message || err));
    }
  }, [user, videos, proofs]);

  const loadSchedules = useCallback(async (filters) => {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => { if (v) params.set(k, v); });
    try {
      const { data } = await api.get(`/admin/schedules${params.toString() ? `?${params.toString()}` : ""}`);
      setSchedules(data);
    } catch (err) {
      log.warn("[admin] schedules load failed:", err?.message || err);
    }
  }, []);
  useEffect(() => {
    if (user?.role === "admin") loadSchedules(scheduleFilters);
  }, [user, scheduleFilters, loadSchedules]);

  if (loading) return <PublicLayout><div className="p-20 text-center">Loading…</div></PublicLayout>;
  if (!user || user.role !== "admin") return <PublicLayout><div className="p-20 text-center">Unauthorized.</div></PublicLayout>;

  const schoolAction = async (id, action) => {
    try { await api.post(`/admin/schools/${id}/action`, { action }); toast.success(`School ${action}d`); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const createBatch = async (schoolId, pending) => {
    const qty = prompt(`Create print batch. Books to print (pending: ${pending})`, String(pending));
    if (!qty) return;
    try { await api.post("/admin/batches", { school_id: schoolId, total_books: parseInt(qty, 10) }); toast.success("Batch created"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const dispatchBatch = async (batchId) => {
    const tid = prompt("Tracking ID?");
    if (!tid) return;
    try { await api.post(`/admin/batches/${batchId}/dispatch`, { tracking_id: tid, dispatch_status: "dispatched" }); toast.success("Dispatched"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const proofAction = async (id, status) => {
    try { await api.post(`/admin/proofs/${id}/action`, { approval_status: status }); toast.success(`Proof ${status}`); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const saveSettings = async () => {
    try { await api.put("/settings", settings); toast.success("Settings saved"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const deleteSchool = async (school) => {
    if (!confirm(`Permanently delete "${school.name}"?\n\nThis removes the school, its login, students, batches, proofs and pledges. It will fail if verified donations exist.`)) return;
    try { await api.delete(`/admin/schools/${school.school_id}`); toast.success(`Deleted ${school.name}`); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const uploadMou = async (schoolId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/schools/${schoolId}/mou`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("MoU uploaded.");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const deleteMou = async (schoolId) => {
    if (!confirm("Remove MoU for this school?")) return;
    try { await api.delete(`/admin/schools/${schoolId}/mou`); toast.success("MoU removed."); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const addVideo = async () => {
    const url = prompt("Instagram URL (https://www.instagram.com/...)");
    if (!url) return;
    const title = prompt("Title (optional)", "") || "";
    try { await api.post("/admin/impact-videos", { video_url: url, title }); toast.success("Video added."); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const toggleVideo = async (v) => {
    try { await api.put(`/admin/impact-videos/${v.video_id}`, { is_active: !v.is_active }); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const deleteVideo = async (id) => {
    if (!confirm("Delete this video?")) return;
    try { await api.delete(`/admin/impact-videos/${id}`); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const sendTestEmail = async () => {
    try {
      const { data } = await api.post("/admin/emails/test");
      if (data.status === "success") toast.success(`Test email sent to ${data.to}`);
      else if (data.status === "skipped") toast.info("SMTP not configured (logged only).");
      else toast.error(`Failed: ${data.error || "unknown"}`);
    } catch (e) { toast.error(formatError(e)); }
  };

  const adminUploadProof = async (schoolId, file, caption) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption || "");
    try {
      await api.post(`/admin/schools/${schoolId}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Proof uploaded & auto-approved.");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const addComic = async (file, title, displayOrder) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title || "");
    fd.append("display_order", String(displayOrder || 1));
    fd.append("is_active", "true");
    try { await api.post("/admin/comics", fd, { headers: { "Content-Type": "multipart/form-data" }}); toast.success("Comic added."); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const toggleComic = async (c) => {
    try { await api.put(`/admin/comics/${c.comic_id}`, { is_active: !c.is_active }); const { data } = await api.get("/admin/comics"); setComics(data); }
    catch (e) { toast.error(formatError(e)); }
  };
  const deleteComic = async (id) => {
    if (!confirm("Delete this comic?")) return;
    try { await api.delete(`/admin/comics/${id}`); const { data } = await api.get("/admin/comics"); setComics(data); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="bb-overline text-[var(--bb-terracotta)] mb-3">ADMIN CONTROL CENTER</div>
        <h1 className="font-serif text-4xl md:text-5xl text-[var(--bb-forest)] tracking-tight">Platform Operations</h1>

        {reports && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-3">
            <Kpi label="Schools" value={reports.schools_count} />
            <Kpi label="Active" value={reports.schools_active} />
            <Kpi label="Sponsorships" value={reports.donations_count} />
            <Kpi label="₹ Collected" value={`₹${(reports.amount_total||0).toLocaleString()}`} />
            <Kpi label="Books" value={reports.books_total} />
            <Kpi label="Pending proofs" value={reports.proofs_pending} />
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-1 border-b border-[var(--bb-border)]">
          {[["reports","Reports"],["schools","Schools"],["orders","Orders"],["batches","Batches"],["donations","Sponsorships"],["pledges","Pledges"],["proofs","Proofs"],["visits","Visits"],["videos","Impact videos"],["comics","Comics"],["ecomics","E-Comics"],["welcome","Welcome images"],["razorpay","API keys"],["settings","Settings"]].map(([k,l]) => (
            <button key={k} onClick={()=>setTab(k)} className={`px-5 py-3 text-sm ${tab===k?"border-b-2 border-[var(--bb-terracotta)] text-[var(--bb-terracotta)]":"text-[var(--bb-muted)]"}`} data-testid={`admin-tab-${k}`}>{l}</button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "reports" && reports && (
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bb-card p-6 md:col-span-2">
                <div className="bb-overline text-[var(--bb-muted)] mb-4 flex items-center gap-2"><LineIcon size={14}/> SPONSORSHIP TREND</div>
                {reports.trend.length === 0 ? <div className="p-8 text-center text-sm text-[var(--bb-muted)]">No data yet.</div> : (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={reports.trend}>
                      <CartesianGrid stroke="#E8E3D7" strokeDasharray="3 3"/>
                      <XAxis dataKey="month" stroke="#6C7A73" fontSize={11}/>
                      <YAxis stroke="#6C7A73" fontSize={11}/>
                      <Tooltip/>
                      <Line type="monotone" dataKey="amount" stroke="#C25946" strokeWidth={2} name="₹ raised"/>
                      <Line type="monotone" dataKey="books" stroke="#1C3F34" strokeWidth={2} name="Books" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="bb-card p-6">
                <div className="bb-overline text-[var(--bb-muted)] mb-4">SNAPSHOT</div>
                <div className="space-y-3 text-sm">
                  <Row k="Total schools" v={reports.schools_count}/>
                  <Row k="Active schools" v={reports.schools_active}/>
                  <Row k="Successful sponsorships" v={reports.donations_count}/>
                  <Row k="Books sponsored" v={reports.books_total}/>
                  <Row k="Batches" v={reports.batches_count}/>
                  <Row k="Proofs pending" v={reports.proofs_pending}/>
                </div>
              </div>
              <AdminEComicsSales />
            </div>
          )}

          {tab === "schools" && (
            <div className="bb-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bb-sand)] bb-overline text-[var(--bb-muted)]"><tr><th className="p-3 text-left">School</th><th className="p-3 text-left">Contact</th><th className="p-3 text-right">Students</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">MoU</th><th className="p-3"></th></tr></thead>
                <tbody>
                  {schools.map(s=>(
                    <tr key={s.school_id} className="border-t border-[var(--bb-border)]" data-testid={`admin-school-${s.school_id}`}>
                      <td className="p-3"><div className="font-medium">{s.name}</div><div className="text-xs text-[var(--bb-muted)]">{s.address}</div></td>
                      <td className="p-3"><div>{s.contact_person}</div><div className="text-xs text-[var(--bb-muted)]">{s.phone}</div></td>
                      <td className="p-3 text-right">{s.total_students}</td>
                      <td className="p-3"><span className="bb-tag">{s.status}</span></td>
                      <td className="p-3">
                        {s.mou_path ? (
                          <div className="flex items-center gap-2 text-xs">
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${s.mou_path}`} target="_blank" rel="noreferrer" className="bb-link inline-flex items-center gap-1" data-testid={`mou-view-${s.school_id}`}><FileText size={12}/> View</a>
                            <button onClick={()=>deleteMou(s.school_id)} className="text-[var(--bb-error)] hover:underline" data-testid={`mou-delete-${s.school_id}`}>Remove</button>
                          </div>
                        ) : (
                          <label className="text-xs text-[var(--bb-forest)] hover:text-[var(--bb-terracotta)] cursor-pointer inline-flex items-center gap-1" data-testid={`mou-upload-${s.school_id}`}>
                            <Upload size={12}/> Upload PDF
                            <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e)=>e.target.files[0] && uploadMou(s.school_id, e.target.files[0])}/>
                          </label>
                        )}
                      </td>
                      <td className="p-3 text-right space-x-2">
                        {s.status === "pending" && (<>
                          <button onClick={()=>schoolAction(s.school_id, "approve")} className="bb-btn-secondary !py-1.5 !px-3 text-xs" data-testid={`approve-${s.school_id}`}><Check size={12} className="inline"/> Approve</button>
                          <button onClick={()=>schoolAction(s.school_id, "reject")} className="text-xs text-[var(--bb-error)] hover:underline" data-testid={`reject-${s.school_id}`}>Reject</button>
                        </>)}
                        <button onClick={()=>deleteSchool(s)} className="text-xs text-[var(--bb-error)] hover:underline inline-flex items-center gap-1" data-testid={`delete-${s.school_id}`} title="Delete school"><Trash2 size={12}/> Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "orders" && (
            <div className="bb-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bb-sand)] bb-overline text-[var(--bb-muted)]"><tr><th className="p-3 text-left">School</th><th className="p-3 text-right">Required</th><th className="p-3 text-right">Sponsored</th><th className="p-3 text-right">Printed</th><th className="p-3 text-right">Pending</th><th className="p-3"></th></tr></thead>
                <tbody>
                  {orders.map(o=>(
                    <tr key={o.school_id} className="border-t border-[var(--bb-border)]">
                      <td className="p-3">{o.school_name}</td>
                      <td className="p-3 text-right">{o.total_books_required}</td>
                      <td className="p-3 text-right">{o.funded_books}</td>
                      <td className="p-3 text-right">{o.printed_books}</td>
                      <td className="p-3 text-right font-medium text-[var(--bb-terracotta)]">{o.pending_to_print}</td>
                      <td className="p-3 text-right">{o.pending_to_print > 0 && <button onClick={()=>createBatch(o.school_id, o.pending_to_print)} className="bb-btn-primary !py-1.5 !px-3 text-xs" data-testid={`batch-${o.school_id}`}><Printer size={12} className="inline"/> Create batch</button>}</td>
                    </tr>
                  ))}
                  {orders.length===0 && <tr><td colSpan="6" className="p-8 text-center text-sm text-[var(--bb-muted)]">No sponsored orders yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "batches" && (
            <div className="bb-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bb-sand)] bb-overline text-[var(--bb-muted)]"><tr><th className="p-3 text-left">Batch</th><th className="p-3 text-left">School</th><th className="p-3 text-right">Books</th><th className="p-3 text-left">Print</th><th className="p-3 text-left">Dispatch</th><th className="p-3 text-left">Tracking</th><th className="p-3"></th></tr></thead>
                <tbody>
                  {batches.map(b=>(
                    <tr key={b.batch_id} className="border-t border-[var(--bb-border)]">
                      <td className="p-3 font-mono text-xs">{b.batch_id}</td>
                      <td className="p-3">{b.school_name}</td>
                      <td className="p-3 text-right">{b.total_books}</td>
                      <td className="p-3"><span className="bb-tag">{b.print_status}</span></td>
                      <td className="p-3"><span className="bb-tag">{b.dispatch_status}</span></td>
                      <td className="p-3 font-mono text-xs">{b.tracking_id || "—"}</td>
                      <td className="p-3 text-right">{b.dispatch_status==="pending" && <button onClick={()=>dispatchBatch(b.batch_id)} className="bb-btn-secondary !py-1.5 !px-3 text-xs" data-testid={`dispatch-${b.batch_id}`}><Truck size={12} className="inline"/> Dispatch</button>}</td>
                    </tr>
                  ))}
                  {batches.length===0 && <tr><td colSpan="7" className="p-8 text-center text-sm text-[var(--bb-muted)]">No batches yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "donations" && (
            <div className="bb-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bb-sand)] bb-overline text-[var(--bb-muted)]"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Sponsor</th><th className="p-3 text-left">School</th><th className="p-3 text-right">Sets</th><th className="p-3 text-right">Amount</th><th className="p-3 text-left">Status</th></tr></thead>
                <tbody>
                  {donations.map(d=>(
                    <tr key={d.donation_id} className="border-t border-[var(--bb-border)]">
                      <td className="p-3">{d.donation_date?.slice(0,10)}</td>
                      <td className="p-3">{d.donor_name}{d.anonymous && <span className="text-xs text-[var(--bb-muted)]"> (anon)</span>}</td>
                      <td className="p-3">{d.school_name}</td>
                      <td className="p-3 text-right">{d.quantity}</td>
                      <td className="p-3 text-right">₹{d.total_amount?.toLocaleString()}</td>
                      <td className="p-3"><span className={`bb-tag ${d.payment_status==="SUCCESS"?"bb-tag-green":""}`}>{d.payment_status}</span></td>
                    </tr>
                  ))}
                  {donations.length===0 && <tr><td colSpan="6" className="p-8 text-center text-sm text-[var(--bb-muted)]">No sponsorships yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "pledges" && (
            <div className="bb-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bb-sand)] bb-overline text-[var(--bb-muted)]"><tr><th className="p-3 text-left">Sponsor</th><th className="p-3 text-left">School</th><th className="p-3 text-right">Qty</th><th className="p-3 text-right">Amount / month</th><th className="p-3 text-right">Charges</th><th className="p-3 text-left">Next charge</th><th className="p-3 text-left">Status</th><th className="p-3"></th></tr></thead>
                <tbody>
                  {pledges.map(p=>(
                    <tr key={p.pledge_id} className="border-t border-[var(--bb-border)]" data-testid={`admin-pledge-${p.pledge_id}`}>
                      <td className="p-3">{p.anonymous ? "Anonymous" : p.donor_name}</td>
                      <td className="p-3">{p.school_name}</td>
                      <td className="p-3 text-right">{p.quantity}</td>
                      <td className="p-3 text-right">₹{p.amount_per_charge?.toLocaleString()}</td>
                      <td className="p-3 text-right">{p.charges_count || 0}</td>
                      <td className="p-3">{p.next_charge_at?.slice(0,10)}</td>
                      <td className="p-3"><span className="bb-tag">{p.status}</span></td>
                      <td className="p-3 text-right text-xs text-[var(--bb-muted)]">Auto-charged by Razorpay</td>
                    </tr>
                  ))}
                  {pledges.length===0 && <tr><td colSpan="8" className="p-8 text-center text-sm text-[var(--bb-muted)]">No pledges yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "proofs" && (
            <div className="space-y-6">
              {/* Admin upload-for-school widget */}
              <AdminProofUpload schools={schools} onUpload={adminUploadProof} />

              <div className="grid md:grid-cols-3 gap-4">
                {proofs.map(p=>(
                  <div key={p.proof_id} className="bb-card p-4">
                    <div className="aspect-video bg-[var(--bb-sand)] rounded overflow-hidden mb-3">
                      {p.type === "image" ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.storage_path}`} alt="" className="w-full h-full object-cover"/> : <div className="grid place-items-center h-full text-[var(--bb-muted)] text-sm">Video / File</div>}
                    </div>
                    <div className="text-xs text-[var(--bb-muted)] mb-1">{p.school_name}{p.uploaded_by === "admin" && <span className="ml-1">· admin</span>}</div>
                    <div className="text-xs text-[var(--bb-muted)]">{p.original_filename}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="bb-tag">{p.approval_status}</span>
                      {p.approval_status === "pending" && (
                        <div className="flex gap-1">
                          <button onClick={()=>proofAction(p.proof_id, "approved")} className="text-xs text-[var(--bb-success)]" data-testid={`proof-approve-${p.proof_id}`}><Check size={14}/></button>
                          <button onClick={()=>proofAction(p.proof_id, "rejected")} className="text-xs text-[var(--bb-error)]" data-testid={`proof-reject-${p.proof_id}`}><X size={14}/></button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {proofs.length===0 && <div className="col-span-full bb-card p-8 text-center text-sm text-[var(--bb-muted)]">No proofs.</div>}
              </div>
            </div>
          )}

          {tab === "comics" && (
            <AdminComicsManager comics={comics} onAdd={addComic} onToggle={toggleComic} onDelete={deleteComic} />
          )}

          {tab === "ecomics" && (
            <AdminEComics />
          )}

          {tab === "welcome" && (
            <AdminWelcomeImages />
          )}

          {tab === "razorpay" && (
            <AdminRazorpayKeys />
          )}

          {tab === "visits" && (
            <AdminSchedules
              schedules={schedules}
              schools={schools}
              filters={scheduleFilters}
              onFilter={(f) => setScheduleFilters(f)}
            />
          )}

          {tab === "videos" && (
            <div className="space-y-4" data-testid="admin-videos-tab">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="font-serif text-2xl text-[var(--bb-forest)]">Impact Videos</h3>
                  <p className="text-xs text-[var(--bb-muted)] mt-1">Instagram Reels / Posts only. Only admin can add, toggle, or delete.</p>
                </div>
                <button onClick={addVideo} className="bb-btn-primary inline-flex items-center gap-2" data-testid="video-add"><Instagram size={14}/> Add Instagram video</button>
              </div>
              {videos.length === 0 ? (
                <div className="bb-card p-10 text-center text-sm text-[var(--bb-muted)]">No videos yet. Add the first Instagram Reel or Post.</div>
              ) : (
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {videos.map(v => (
                    <div key={v.video_id} className="bb-card p-4" data-testid={`admin-video-${v.video_id}`}>
                      <div className="aspect-square bg-[var(--bb-sand)] rounded overflow-hidden mb-3 relative">
                        {v.thumbnail_url ? (
                          <img src={v.thumbnail_url} alt="" onError={(e)=>{e.currentTarget.style.display='none'}} className="w-full h-full object-cover"/>
                        ) : (
                          <div className="w-full h-full grid place-items-center text-[var(--bb-muted)]"><PlayCircle size={40}/></div>
                        )}
                        <span className={`absolute top-2 right-2 bb-tag ${v.is_active ? "bb-tag-green" : ""}`}>{v.is_active ? "Active" : "Hidden"}</span>
                      </div>
                      <div className="text-sm text-[var(--bb-ink)] line-clamp-1 mb-1">{v.title || "Untitled"}</div>
                      <a href={v.video_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--bb-muted)] bb-link line-clamp-1 block">{v.video_url}</a>
                      <div className="mt-3 flex items-center justify-between text-xs">
                        <button onClick={()=>toggleVideo(v)} className="bb-link" data-testid={`video-toggle-${v.video_id}`}>{v.is_active ? "Hide" : "Show"}</button>
                        <button onClick={()=>deleteVideo(v.video_id)} className="text-[var(--bb-error)] hover:underline inline-flex items-center gap-1" data-testid={`video-delete-${v.video_id}`}><Trash2 size={12}/> Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "settings" && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
              <div className="bb-card p-8">
                <div className="bb-overline text-[var(--bb-muted)] mb-3 flex items-center gap-2"><Settings2 size={14}/> PLATFORM PRICING</div>
                <p className="text-xs text-[var(--bb-muted)] mb-5">Changes apply only to future sponsorships. Past sponsorships keep their snapshot.</p>
                <div className="space-y-5">
                  <div>
                    <label className="bb-overline text-[var(--bb-muted)]">Price per set (₹)</label>
                    <input type="number" min="1" value={settings.price_per_set} onChange={(e)=>setSettings({...settings, price_per_set: parseInt(e.target.value||"0",10)})} className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] font-serif text-3xl" data-testid="setting-price"/>
                  </div>
                  <div>
                    <label className="bb-overline text-[var(--bb-muted)]">Minimum sponsorship (sets)</label>
                    <input type="number" min="1" value={settings.minimum_donation_sets} onChange={(e)=>setSettings({...settings, minimum_donation_sets: parseInt(e.target.value||"0",10)})} className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] font-serif text-3xl" data-testid="setting-min"/>
                  </div>
                  <div className="pt-4 border-t border-[var(--bb-border)]">
                    <div className="bb-overline text-[var(--bb-muted)] mb-3 flex items-center gap-2"><Mail size={12}/> SPONSOR EMAILS</div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={settings.donor_emails_enabled} onChange={(e)=>setSettings({...settings, donor_emails_enabled: e.target.checked})} data-testid="setting-emails"/>
                      Send confirmation emails after successful sponsorships
                    </label>
                    <button type="button" onClick={sendTestEmail} className="mt-3 bb-btn-secondary text-xs !py-1.5 !px-4" data-testid="send-test-email">Send test email to myself</button>
                  </div>
                </div>
              </div>
              <div className="bb-card p-6">
                <div className="bb-overline text-[var(--bb-muted)] mb-3 flex items-center gap-2"><History size={14}/> CHANGE HISTORY</div>
                {history.length === 0 ? <div className="p-6 text-center text-sm text-[var(--bb-muted)]">No changes yet.</div> : (
                  <ul className="text-sm divide-y divide-[var(--bb-border)]">
                    {history.map(h=>(
                      <li key={h.history_id} className="py-3 flex items-center justify-between">
                        <div>
                          <div>₹{h.snapshot?.price_per_set} / set · min {h.snapshot?.minimum_donation_sets}</div>
                          <div className="text-xs text-[var(--bb-muted)]">{h.changed_at?.slice(0,16).replace("T"," ")}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              </div>

              <AdminAudioComicsSettings settings={settings} setSettings={setSettings} />

              <div className="flex justify-end">
                <button onClick={saveSettings} className="bb-btn-primary" data-testid="save-settings">Save all settings</button>
              </div>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}

